import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { updateProfile, updateEmail, updatePassword, EmailAuthProvider, reauthenticateWithCredential, deleteUser } from 'firebase/auth';
import { trackButtonClick, trackFormSubmission } from '../utils/analytics';
import '../styles/auth.css';

const Account: React.FC = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [activeSection, setActiveSection] = useState('logout');
  
  // Check if the user signed in with Google
  const isGoogleUser = currentUser?.providerData.some(
    provider => provider.providerId === 'google.com'
  );

  useEffect(() => {
    if (currentUser) {
      setName(currentUser.displayName || '');
      setEmail(currentUser.email || '');
    }
  }, [currentUser]);

  const handleLogout = async () => {
    try {
      setLoading(true);
      await logout();
      trackButtonClick('logout', 'account_page');
      navigate('/login');
    } catch (error) {
      setMessage({ text: 'Failed to log out', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) return;

    try {
      setLoading(true);
      setMessage({ text: '', type: '' });

      if (name !== currentUser.displayName) {
        await updateProfile(currentUser, { displayName: name });
      }

      if (email !== currentUser.email) {
        if (isGoogleUser) {
          throw new Error('Email cannot be changed for Google accounts. Please update your email in your Google account settings.');
        }
        
        if (!currentPassword) {
          throw new Error('Current password is required to update email');
        }
        
        // Re-authenticate user before email update
        const credential = EmailAuthProvider.credential(
          currentUser.email || '',
          currentPassword
        );
        await reauthenticateWithCredential(currentUser, credential);
        await updateEmail(currentUser, email);
      }

      trackFormSubmission('profile_update_form');
      setMessage({ text: 'Profile updated successfully!', type: 'success' });
    } catch (error) {
      if (error instanceof Error) {
        setMessage({ text: error.message, type: 'error' });
      } else {
        setMessage({ text: 'Failed to update profile', type: 'error' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) return;

    // Validate passwords
    if (newPassword !== confirmPassword) {
      setMessage({ text: 'New passwords do not match', type: 'error' });
      return;
    }

    if (newPassword.length < 6) {
      setMessage({ text: 'Password should be at least 6 characters', type: 'error' });
      return;
    }

    try {
      setLoading(true);
      setMessage({ text: '', type: '' });

      // Re-authenticate user before password update
      const credential = EmailAuthProvider.credential(
        currentUser.email || '',
        currentPassword
      );
      await reauthenticateWithCredential(currentUser, credential);
      
      // Update password
      await updatePassword(currentUser, newPassword);
      
      // Clear password fields
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      
      trackFormSubmission('password_update_form');
      setMessage({ text: 'Password updated successfully!', type: 'success' });
    } catch (error) {
      if (error instanceof Error) {
        setMessage({ text: error.message, type: 'error' });
      } else {
        setMessage({ text: 'Failed to update password', type: 'error' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!currentUser) return;
    
    if (window.confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
      try {
        setLoading(true);
        setMessage({ text: '', type: '' });
        
        // Handle differently based on authentication method
        if (isGoogleUser) {
          // For Google users, we can directly attempt to delete without reauthentication
          // Note: This might still fail depending on how recently the user signed in
          await deleteUser(currentUser);
        } else {
          // Re-authenticate email/password user before deletion
          if (!currentPassword) {
            setMessage({ text: 'Current password is required to delete account', type: 'error' });
            setLoading(false);
            return;
          }
          
          const credential = EmailAuthProvider.credential(
            currentUser.email || '',
            currentPassword
          );
          await reauthenticateWithCredential(currentUser, credential);
          
          // Delete the user
          await deleteUser(currentUser);
        }
        
        trackButtonClick('delete_account', 'account_page');
        navigate('/signup');
      } catch (error) {
        if (error instanceof Error) {
          // If we receive an auth/requires-recent-login error, guide the user
          if (error.message.includes('auth/requires-recent-login')) {
            setMessage({ 
              text: 'For security reasons, you need to log out and log in again before deleting your account.',
              type: 'error' 
            });
          } else {
            setMessage({ text: error.message, type: 'error' });
          }
        } else {
          setMessage({ text: 'Failed to delete account', type: 'error' });
        }
      } finally {
        setLoading(false);
      }
    }
  };

  if (!currentUser) {
    return <div className="auth-container">Loading...</div>;
  }

  return (
    <div className="container">
      <h1 className="page-title text-center">Account Settings</h1>
      
      <div className="card" style={{ maxWidth: '700px', margin: '0 auto' }}>
        <div className="account-tabs">
          <button 
            className={`account-tab ${activeSection === 'profile' ? 'active' : ''}`}
            onClick={() => setActiveSection('profile')}
          >
            Update Profile
          </button>
          {!isGoogleUser && (
            <button 
              className={`account-tab ${activeSection === 'security' ? 'active' : ''}`}
              onClick={() => setActiveSection('security')}
            >
              Security
            </button>
          )}
          <button 
            className={`account-tab ${activeSection === 'logout' ? 'active' : ''}`}
            onClick={() => setActiveSection('logout')}
          >
            Logout
          </button>
        </div>
        
        {message.text && (
          <div className={`auth-${message.type}`}>
            {message.text}
          </div>
        )}
        
        {activeSection === 'profile' && (
          <form onSubmit={handleProfileUpdate} className="auth-form">
            <div className="form-group">
              <label htmlFor="name">Name</label>
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="form-control"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="form-control"
                disabled={isGoogleUser}
              />
              {isGoogleUser && (
                <small className="form-text text-muted">
                  Your email is managed by Google and cannot be changed here.
                </small>
              )}
            </div>
            
            {(email !== currentUser?.email && !isGoogleUser) && (
              <div className="form-group">
                <label htmlFor="currentPassword">Current Password (required to change email)</label>
                <input
                  type="password"
                  id="currentPassword"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required={email !== currentUser?.email}
                  className="form-control"
                />
              </div>
            )}
            
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Updating...' : 'Update Profile'}
            </button>
          </form>
        )}
        
        {activeSection === 'security' && !isGoogleUser && (
          <>
            <form onSubmit={handlePasswordUpdate} className="auth-form">
              <h3>Change Password</h3>
              <div className="form-group">
                <label htmlFor="currentPassword">Current Password</label>
                <input
                  type="password"
                  id="currentPassword"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                  className="form-control"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="newPassword">New Password</label>
                <input
                  type="password"
                  id="newPassword"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  className="form-control"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm New Password</label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="form-control"
                />
              </div>
              
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Updating...' : 'Update Password'}
              </button>
            </form>
            
            <div className="danger-zone">
              <h3>Danger Zone</h3>
              <p>Once you delete your account, there is no going back. Please be certain.</p>
              <div className="form-group">
                <label htmlFor="deletePassword">Enter your password to delete account</label>
                <input
                  type="password"
                  id="deletePassword"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Password"
                  required
                  className="form-control"
                />
              </div>
              <button 
                onClick={handleDeleteAccount} 
                className="delete-button"
                disabled={loading || !currentPassword}
              >
                Delete Account
              </button>
            </div>
          </>
        )}

        {activeSection === 'security' && isGoogleUser && (
          <div className="danger-zone">
            <h3>Danger Zone</h3>
            <p>Once you delete your account, there is no going back. Please be certain.</p>
            <p>Since you're signed in with Google, you don't need to enter a password to delete your account.</p>
            <button 
              onClick={handleDeleteAccount} 
              className="delete-button"
              disabled={loading}
            >
              Delete Account
            </button>
          </div>
        )}

        {activeSection === 'logout' && (
          <div className="logout-tab">
            <div className="account-info">
              <h3>Account Information</h3>
              <p><strong>Email:</strong> {currentUser.email}</p>
              <p><strong>Name:</strong> {currentUser.displayName || 'Not set'}</p>
              <p><strong>Sign-in Method:</strong> {isGoogleUser ? 'Google' : 'Email/Password'}</p>
              {/* {currentUser.metadata && (
                <p><strong>Account created:</strong> {new Date(parseInt(currentUser.metadata.creationTime || '0')).toLocaleDateString()}</p>
              )} */}
            </div>
            
            <div className="logout-section">
              <h3>Log Out</h3>
              <p>You will be securely logged out of your account on this device.</p>
              <button 
                onClick={handleLogout} 
                className="btn btn-primary"
                disabled={loading}
              >
                Log Out
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Account; 